"use strict";
/* eslint-disable camelcase */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableName = exports.ALL_TABLES = void 0;
/**
 * Dependency-free source of truth for database table names.
 *
 * This module intentionally imports NOTHING. `ALL_TABLES`, the `DbTables` type, and the
 * `TableName` constants are all pure string literals, so keeping them isolated in a leaf module
 * means referencing a table name never drags in the heavy import graph of `DbTables.ts`, and can
 * never introduce a circular dependency.
 *
 * `DbTables.ts` re-exports everything here for backwards compatibility.
 */
/**
 * Every table in the (live) `public` schema. This is the single source of truth for valid table
 * names; the {@link DbTables} type and the {@link TableName} constants are both derived from it.
 *
 * Historically this list was split into `SHADOWED_TABLES` / `NON_SHADOWED_TABLES` /
 * `SANDBOX_ONLY_TABLES` to support the (now-removed) sandbox-mode feature and its `schemaSync`
 * verification. With sandbox gone there is only one schema, so the split has been collapsed.
 */
exports.ALL_TABLES = [
    // *** Formerly shadowed tables (writable in the old sandbox schema). ***
    'borrowing_base_pipeline_artifact_instances',
    'master_loan_tapes',
    'assigned_loan_tapes',
    'receivables',
    'receivable_events',
    'reports',
    'reports_v2',
    'reports_to_entity_map',
    'cash_balances_feed_contexts',
    'cash_balances_feed_screenshots',
    'manual_bank_account_reports',
    'bank_account_reports',
    'cash_response_tapes',
    'borrowing_base_reports',
    'borrowing_base_report_attachments',
    'borrowing_base_report_loan_product_calculations',
    'funding_request_constraints',
    'eligibility_criteria_calculations',
    'concentration_limit_calculations',
    'accounting_accounts',
    'accounting_transactions',
    'accounting_entries',
    'events',
    'event_logs',
    'funding_request_verification_reports',
    'funding_request_approval_reports',
    'funding_requests',
    'funding_request_components',
    'proforma_funding_request_components_map',
    'credit_agreements',
    'credit_agreement_parties',
    'funding_request_receivable_eligibility_verification',
    'borrower_cash_balances_feed',
    'borrowing_base_pipeline_artifact_documents',
    'borrowing_base_pipeline_artifacts',
    'borrowing_base_pipeline_artifact_dependencies',
    'borrowing_base_pipeline_runs',
    'graph_run',
    'borrowing_base_pipeline_deleted_instances', // @pipeline_runs: this is a temporary table for pipeline_run_table migration
    'internal_repayment_tapes',
    'assigned_repayment_tapes',
    'repayment_entries_credit_facility',
    'tape_builder_graph_nodes',
    // *** Formerly non-shadowed tables (live-only in the old sandbox setup). ***
    'assigned_loan_tape_reports',
    'assignment_confirmation_jobs',
    'assignment_strategies',
    'assignment_strategy_loan_product_criteria',
    'async_operations',
    'athena_queries',
    'bank_accounts',
    'bank_institutions',
    'borrowing_base_pipeline_definitions',
    'borrowing_base_servicer_reports',
    'compliance_reports_cf',
    'cdq_config',
    'cdq_acknowledgements',
    'concentration_limit_multi_loan_product_calculations',
    'connected_organizations',
    'covenant_loan_tapes',
    'credit_facilities',
    'credit_facility_balance_sheet_data',
    'credit_facility_reports',
    'credit_facility_state',
    'org_provided_inputs',
    'org_provided_values',
    // *** Credit Facility Loans ***
    'credit_facility_loan_actions',
    'credit_facility_loan_connections',
    'credit_facility_lender_shares',
    'credit_facility_loans',
    'credit_facility_loan_pricing_options',
    'credit_facility_lender_shares_transactions',
    'credit_facility_lender_share_reports',
    'lender_share_funding_components',
    'credit_facility_batch_settlements',
    'credit_facility_batch_settlement_loan_connections',
    'credit_facility_batch_settlement_reports',
    // *** Credit Facility - Other Products (not loans) ***
    'credit_facility_contingent_liabilities',
    'credit_facility_contingent_liability_transactions',
    'credit_facility_documents',
    'datalake_tables',
    'datalake_hydration_event_jobs',
    'deliverable_tasks',
    'deliverables_artifacts_instances',
    'deliverables_artifacts',
    'deliverables_containers',
    'deliverables_containers_to_threads_mapping',
    'deliverables_containers_receipts',
    'deliverables_containers_receipts_actions_log',
    'deliverables_containers_generate_receipts',
    'deliverables_artifacts_instances_datalake_hydration_status',
    // next two tables are deliverables workflows tables
    'deliverables_containers_receipt_workflows_map',
    'deliverables_containers_receipt_user_approvals',
    'explo_placement_integrations',
    'external_metadata',
    'financial_statement_values',
    'financial_statements',
    'financial_statement_line_items',
    'financial_covenants',
    'finley_ai_knowledge_base_documents',
    'finley_ai_threads',
    'finley_ai_prompt_responses',
    'finley_ai_prompt_templates',
    'funding_request_reports',
    // following two tables are FR workflow tables
    'funding_request_user_approvals',
    'funding_request_workflow_instance_map',
    'funding_requests_to_threads_mapping',
    'groups',
    'group_memberships',
    'implementations',
    'implementation_pipelines',
    'invoices',
    'invoice_components',
    'invoice_items',
    'invoice_consolidated_disbursements',
    'invoice_disbursements',
    'invoice_disbursement_items',
    'invoice_disbursement_items_transactions',
    'invoice_payment_in_kind_items',
    'invoice_payment_in_kind_items_transactions',
    'invoice_reports',
    'loan_products',
    'loan_product_balance_sheet_data',
    'materialized_view_configuration',
    'metric_alert_state',
    'metric_alert_thresholds',
    'organizations',
    'api_keys',
    'plaid_asset_report_accounts',
    'plaid_asset_reports',
    'plaid_institution_accounts',
    'plaid_items',
    'portfolio_covenants',
    'railz_financial_api_results',
    'railz_integrations',
    'receivable_documentation_generated',
    'receivable_documentation_raw',
    'receivable_documentation_verification_results',
    'receivable_response_tape_preview_attachments',
    'receivable_response_tape_preview_jobs',
    'receivable_response_tape_previews',
    'receivable_response_tape_preview_reports',
    'reporting_docx_template_customizations',
    'reporting_generate_worklease_status',
    'report_templates',
    'report_xlsx_template_properties',
    'teller_institution_accounts',
    'users',
    'webhook_attempts',
    'webhook_subscriptions',
    'valley_bank_van_cust',
    'valley_bank_van_dep_acct',
    'valley_bank_van_cust_acct_rel',
    'valley_bank_van_list',
    'valley_bank_van_product',
    'valley_bank_loan_cust_rel',
    'valley_bank_cuo_deposit',
    'valley_bank_cuo_collat',
    'valley_bank_collat_cust_rel',
    'valley_bank_ps_plus_portfolio',
    'valley_bank_ps_plus_account',
    'valley_bank_ps_plus_sec_detail',
    'valley_bank_ps_plus_sec_master',
    'valley_bank_van_cmmt',
    'valley_bank_van_loan_acct',
    'valley_bank_cuo_loan',
    'valley_bank_cuo_maint',
    'valley_bank_rrt', // FinleyValleyBankResponseTapeRow
    'valley_bank_portfolio_rrt', // portfolio view of rrt
    'valley_bank_assignment_queue',
    'valley_bank_assignment_event_log',
    'valley_bank_collateral_account_stats',
    'valley_bank_loan_account_stats',
    'valley_bank_loan_accounts_to_threads_mapping',
    'valley_bank_historical_loan_account_to_pinned_comment',
    'valley_bank_derivative_path',
    'valley_ibs_api_records',
    'valley_sls_ibs_transaction_tapes',
    'valley_sls_ibs_transactions',
    'valley_sls_ibs_transaction_groups',
    'valley_sls_ibs_transaction_mappings',
    'receivable_borrowing_base_test_waivers',
    'statements',
    'threads',
    'comments',
    'domain_to_threads_mapping',
    'table_preferences',
    'thread_permissions',
    'thread_user_actions_log',
    'workflow_templates',
    'workflow_instances',
    'workflow_nodes',
    'workflow_edges',
    'workflow_actions',
    'workflow_conditions',
    'tasks',
    'permissions',
    'roles',
    'role_permissions',
    'role_assignments',
    'limited_partners',
    'limited_partners_to_loan_product_mapping',
    'limited_partner_match_metadata',
    'limited_partner_tape_records',
    'config_store',
    'attributes',
    'attribute_values',
    'attribute_lists',
    'lender_interest_rates',
    'trial_balance_reports',
    // *** Tables that were referenced in live code but missing from the original sandbox-era lists. ***
    // Surfaced when call sites were migrated from bare `.table('...')` strings (which evade
    // type-checking) to `TableName.*`. Each is a real, current table with a create migration.
    'covenant_calculations',
    'deliverable_items',
    'email_user_subscriptions',
    'floating_interest_rates',
    'interest_rate_benchmarks',
    'interest_rates',
    'notification_disabled_flags',
    'quicksight_dashboards',
    'security_principals',
    'slack_installations',
    'slack_subscriptions',
    'teller_account_reports',
    'teller_items',
    'valley_bank_collateral_to_loan_mapping',
    // *** Further real tables reconciled by cross-checking ALL_TABLES against the live `public`
    // schema. These exist (each has a create migration) but were never registered here, so `DbTables`
    // did not actually cover the full schema. ***
    'ai_agent_workflows',
    'api_copy_state',
    'assignment_strategy_eligibility_exclusions',
    'deliverable_state',
    'deliverables',
    'deliverables_dependencies',
    'deliverables_permissions',
    'deliverables_responsible_parties',
    'deliverables_submissions',
    'email_digest_state',
    'encoded_internal_params',
    'receivable_response_tape_preview_eligibility_statuses',
    'report_docx_template_table_columns',
    'report_docx_template_table_entities',
    'report_docx_template_variables',
    'sftp_copy_log',
    'sftp_copy_state',
    'snowflake_copy_state',
    'test_api_responses',
    'valley_bank_state',
];
/**
 * Identity map of every table name, derived from {@link ALL_TABLES}. Prefer referencing tables via
 * `TableName.<table>` over bare string literals in application code: because the key is a member of
 * {@link DbTables}, a rename or removal of a table in {@link ALL_TABLES} turns every stale reference
 * into a compile error, whereas a raw `.table('...')` string silently passes type-checking.
 *
 * Note: this intentionally does NOT apply to frozen DB migrations/seeds, which must keep the literal
 * table name as it existed when the migration was written.
 */
exports.TableName = Object.fromEntries(exports.ALL_TABLES.map((table) => [table, table]));
//# sourceMappingURL=TableNames.js.map